import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';

export default function TrialNotification({ companyData }) {
  const { t } = useTranslation();
  const [trialInfo, setTrialInfo] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (companyData) {
      const now = new Date();
      const trialEnd = new Date(companyData.trialEndDate);
      const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
      
      setTrialInfo({
        daysLeft: Math.max(0, daysLeft),
        isExpired: now > trialEnd,
        isActive: companyData.isTrialActive && !companyData.isPaid
      });
    }
  }, [companyData]);

  if (!trialInfo || !trialInfo.isActive) {
    return null;
  }

  const handleUpgrade = () => {
    // Navigate to upgrade page (you can create this later)
    navigate('/upgrade');
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  // Different styles based on trial status
  const getNotificationStyle = () => {
    if (trialInfo.isExpired) {
      return {
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        color: 'white'
      };
    } else if (trialInfo.daysLeft <= 1) {
      return {
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white'
      };
    } else {
      return {
        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        color: 'white'
      };
    }
  };

  const getMessage = () => {
    if (trialInfo.isExpired) {
      return t('trial.expired');
    } else if (trialInfo.daysLeft === 0) {
      return t('trial.expiresToday');
    } else if (trialInfo.daysLeft === 1) {
      return t('trial.expiresTomorrow');
    } else {
      return t('trial.daysRemaining', { days: trialInfo.daysLeft });
    }
  };

  return (
    <div style={{
      ...getNotificationStyle(),
      padding: '12px 20px',
      textAlign: 'center',
      position: 'relative',
      fontSize: '14px',
      fontWeight: 500,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{ flex: 1 }}>
          <span style={{ marginRight: '8px' }}>
            {getMessage()}
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handleUpgrade}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            {trialInfo.isExpired ? t('trial.upgradeNowExpired') : t('trial.upgradeNow')}
          </button>
          
          <button
            onClick={handleDismiss}
            style={{
              background: 'transparent',
              color: 'white',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
            title={t('trial.close')}
            aria-label={t('trial.close')}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
} 